import { useCallback, useEffect, useMemo, useState } from 'react';
import { MAX_FILE_SIZE } from 'constants/index';
import { Form, message, Upload } from 'components';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CommercialOfferActions, OfferActionsTypes } from 'models/CommercialOffer';
import {
  useAttachmentTypeQuery,
  useCommercialOfferGetByIdQuery,
  useCurrentUserQuery,
  useDealByIdQuery,
  useLazyPurchaseRequestEmailTemplateQuery,
  useSendOfferMutation,
} from 'api';
import { ContactPersonData } from 'components/common/contactPerson/contactPersonCard';
import { ADMIN_ROUTES } from 'constants/routes';

export interface IEmailTemplate {
  content: string;
  subject: string;
}

/*
async function urlToFile(url: string, fileName: string): Promise<File> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();
  const contentType = response.headers.get('Content-Type') || 'application/octet-stream';

  return new File([blob], fileName, { type: contentType });
}
*/

export const useSendOffer = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notificationApi = useNotificationApi();
  const [searchParams] = useSearchParams();
  const actionType =
    (searchParams.get('action') as OfferActionsTypes) || CommercialOfferActions.SEND_COMMERCIAL_OFFER.value;
  const { id, offerId } = useParams<{ id: string; offerId: string }>();
  const userId = JSON.parse(localStorage.getItem('userId') || '');

  // State
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<{ type: string | 'null'; file: File; existing?: boolean }[]>([]);
  const [error, setError] = useState('');
  const [disabledActions, setDisabledActions] = useState(true);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<IEmailTemplate>({ content: '', subject: '' });

  // API Hooks
  const { data: currentUser = {} } = useCurrentUserQuery({ userId }, { skip: !userId });
  const [triggerFetchTemplate] = useLazyPurchaseRequestEmailTemplateQuery();
  const { data: dealDetails = {}, isLoading: isDealLoading } = useDealByIdQuery({ id: id as string }, { skip: !id });
  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName: 'DEAL' });
  const { data: offerData = [] } = useCommercialOfferGetByIdQuery(
    { commercialOfferId: offerId as string },
    { skip: !userId }
  );
  const [sendOffer] = useSendOfferMutation();
  const { handleSuccess, handleError } = useNotificationMessages();

  const contactPersons = useMemo(() => {
    return (
      dealDetails.client?.contactPersonList?.map((i: ContactPersonData) => {
        return { value: i.id, label: i.fullName, languageId: i.languageId, contacts: i.contacts };
      }) || []
    );
  }, [dealDetails]);

  useEffect(() => {
    if (currentUser) {
      form.setFieldValue('from', currentUser.email);
    }
    const formattedAttachments: { type: string; file: File }[] = [];
    if (offerData.data?.attachments) {
      formattedAttachments.push({
        type: offerData.data.attachments.attachmentType,
        file: {
          ...offerData.data.attachments,
          name: offerData.data.attachments.fileName,
        },
      });
    }
    if (offerData.data?.clientEmail) {
      formattedAttachments.push({
        type: offerData.data.clientEmail.attachmentType,
        file: {
          ...offerData.data.clientEmail,
          name: offerData.data.clientEmail.fileName,
        },
      });
    }
    setAttachments(formattedAttachments);
  }, [currentUser, form, offerData.data]);

  useEffect(() => {
    setDisabledActions(!fileList?.length);
  }, [fileList, attachmentType]);

  const getLanguageTemplate = useCallback(
    async (languageId: string) => {
      const { data: emailTemplate } = await triggerFetchTemplate({ languageId });

      setEmailTemplate((oState) => {
        if (!languageId) {
          return oState;
        } else {
          const { content, subject } = emailTemplate.data;
          form.setFieldValue('subject', subject);
          return { content, subject };
        }
      });
    },
    [form, triggerFetchTemplate]
  );

  const handleEmailTemplateChange = useCallback((value: string, key: 'subject' | 'content') => {
    setEmailTemplate((oState: IEmailTemplate) => ({
      ...oState,
      [key]: value,
    }));
  }, []);

  const handleContactPersonChange = useCallback(
    (value: string) => {
      const selectedPerson = contactPersons.find((person: any) => person.value === value);
      form.setFieldsValue({ contactPersonId: value });

      // If contact person has no email, show warning
      if (selectedPerson && !selectedPerson.contacts?.EMAIL?.length) {
        message.warning('Selected contact person has no email address');
      }

      // If contact person has email, set it by default selected
      if (selectedPerson?.languageId) {
        form.setFieldValue('languageId', selectedPerson.languageId);
        getLanguageTemplate(selectedPerson.languageId);
      }
    },
    [form, contactPersons, getLanguageTemplate]
  );

  const onLanguageChange = useCallback(
    (value: string) => {
      getLanguageTemplate(value);
    },
    [getLanguageTemplate]
  );

  const handleAddAttachment = useCallback(() => {
    if (fileList.length > 0) {
      setAttachments((prev) => [...prev, { type: attachmentType || (null as any), file: fileList[0] }]);
      setAttachmentType(undefined);
      setFileList([]);
    }
  }, [fileList, attachmentType]);

  const handleDeleteAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  }, []);

  const handleFileSizeValidation = useCallback((file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE;
    }

    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setIsPreviewVisible(true);
      };
      reader.readAsDataURL(file);
    }

    setFileList([file]);
    setError('');
    return false;
  }, []);

  const handleCancelAttachment = useCallback(() => {
    setAttachmentType(undefined);
    setFileList([]);
  }, []);

  const handleCancel = useCallback(() => {
    history.back();
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!currentUser?.email?.trim()) {
      notificationApi({ type: 'error', message: 'Sender email is required' });
      return;
    }

    if (!emailTemplate?.content) {
      notificationApi({ type: 'error', message: 'Email body is required' });
      return;
    }
    setDisabledSubmit(true);
    const formData = new FormData();
    formData.append('dealId', id as string);
    formData.append('offerId', offerId as string);
    formData.append('actionType', actionType);
    formData.append('clientId', dealDetails?.client?.clientId);
    formData.append('subject', emailTemplate.subject);
    formData.append('languageId', form.getFieldValue('languageId'));
    formData.append('contactPerson', form.getFieldValue('contactPersonId'));
    formData.append(
      'attachmentType',
      attachments
        .filter(
          (attachment) => attachment.type !== 'COMMERCIAL_OFFER' && attachment.type !== 'EMAIL' && !attachment.existing
        )
        .map((i) => i.type || 'null')
        .join(',')
    );
    formData.append('email', currentUser.email);
    attachments.forEach((i: any) => {
      if (i.type === 'COMMERCIAL_OFFER' && !i.existing) {
        formData.append('commercialOfferAttachmentId', i.file.id);
        formData.append('commercialOfferFileName', i.file.fileName);
      } else if (i.type === 'EMAIL' && !i.existing) {
        const url = new URL(i.file.files['ATTACHMENTS']);
        const attachmentId = url.searchParams.get('attachmentId');
        formData.append('clientEmailId', attachmentId ?? '');
        formData.append('clientEmailFileName', i.file.fileName);
      } else if (i.file?.createDate) {
        formData.append('attachmentId', i.file.id);
        formData.append('attachmentName', i.file.fileName);
      } else {
        formData.append('files', i.file as any, i.file.name);
      }
    });
    formData.append('emailBody', emailTemplate.content);

    try {
      const response: any = await sendOffer({ data: formData, commercialOfferId: offerId });
      if (response?.data?.success) {
        navigate(`${ADMIN_ROUTES.deals.view}/${id}/commercial-offers`);
        handleSuccess('Commercial Offer was successfully sent.');
      } else {
        if (response?.error?.data?.data) {
          handleError(response.error);
        } else {
          notificationApi({ type: 'error', message: response?.error?.data?.message || 'Something went wrong!' });
        }
      }
    } catch (err) {
      handleError(err);
    } finally {
      setDisabledSubmit(false);
    }
  }, [
    currentUser,
    emailTemplate,
    id,
    offerId,
    actionType,
    dealDetails,
    form,
    attachments,
    sendOffer,
    navigate,
    handleSuccess,
    handleError,
    notificationApi,
  ]);

  const onSaveDealAttachments = (_attachments: any) => {
    setAttachments((prev) => [
      ...prev,
      ..._attachments.map((i: { type: string; file: File; existing?: boolean }) => ({
        type: i.type,
        file: i.file,
        existing: i.existing,
      })),
    ]);
  };

  return {
    id,
    form,
    contactPersons,
    isDealLoading,
    actionType,
    dealDetails,
    attachmentTypes,
    currentUser,
    fileList,
    error,
    attachmentType,
    attachments,
    emailTemplate,
    disabledActions,
    disabledSubmit,
    previewImage,
    isPreviewVisible,
    setAttachmentType,
    setFileList,
    setIsPreviewVisible,
    handleContactPersonChange,
    onLanguageChange,
    handleEmailTemplateChange,
    handleAddAttachment,
    handleDeleteAttachment,
    handleFileSizeValidation,
    handleCancelAttachment,
    handleCancel,
    handleSubmit,
    onSaveDealAttachments,
  };
};
